import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { ADMIN_EMAILS } from '../lib/constants';
import { STORAGE_CACHE_KEY, SAMPLE_INITIAL_REPORTS } from '../lib/riskConstants';
import RiskQuickReportForm from '../components/RiskQuickReportForm';
import { SeverityGuideModal } from '../components/risk/SeverityGuideModal';
import GuidedRiskReportWizard from '../components/risk/GuidedRiskReportWizard';

const STATUS_MAP = {
  pending: { label: 'รอตรวจสอบ', step: 1, color: '#d97706', bg: '#fef3c7', icon: '🟡' },
  investigating: { label: 'กำลังสอบสวน RCA', step: 2, color: '#2563eb', bg: '#dbeafe', icon: '🔵' },
  in_progress: { label: 'กำลังดำเนินการ', step: 2, color: '#2563eb', bg: '#dbeafe', icon: '🔵' },
  resolved: { label: 'ดำเนินการแก้ไขแล้ว', step: 3, color: '#16a34a', bg: '#dcfce7', icon: '🟢' },
  action_taken: { label: 'ดำเนินการแก้ไขแล้ว', step: 3, color: '#16a34a', bg: '#dcfce7', icon: '🟢' },
  closed: { label: 'ปิดเคสเรียบร้อย', step: 4, color: '#475569', bg: '#f1f5f9', icon: '⚪' }
};

const getStatusConfig = (status) => {
  return STATUS_MAP[status] || STATUS_MAP.pending;
};

const DOMAIN_LABEL_MAP = {
  clinical: { label: '🩺 คลินิกทันตกรรม', bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
  cssd: { label: '🧼 งานจ่ายกลาง CSSD', bg: '#f0f9ff', color: '#0369a1', border: '#bae6fd' },
  non_clinical: { label: '🏢 ทั่วไป/สิ่งแวดล้อม', bg: '#f8fafc', color: '#475569', border: '#e2e8f0' }
};

const getSeverityBadge = (level) => {
  const lvl = String(level || 'A').toUpperCase().trim();
  const isHigh = ['G', 'H', 'I', '4', '5'].includes(lvl);
  const isMed = ['E', 'F', '3'].includes(lvl);
  const bg = isHigh ? '#fef2f2' : isMed ? '#fffbeb' : '#f0fdf4';
  const color = isHigh ? '#dc2626' : isMed ? '#d97706' : '#16a34a';
  const border = isHigh ? '#fecaca' : isMed ? '#fde68a' : '#bbf7d0';
  return { label: `Level ${lvl}`, bg, color, border };
};

/**
 * RiskPortalStandalonePage
 * หน้าพอร์ทัลรายงานอุบัติการณ์และความเสี่ยงทันตกรรมและงานจ่ายกลางแบบสแตนด์อโลน (risk.fangdent.com / ?portal=risk)
 * ให้บริการแบบ Frictionless ไม่บังคับล็อกอินสำหรับบุคลากรทั่วไปและเจ้าหน้าที่หน้างาน
 */
const RiskPortalStandalonePage = ({
  onNavigateToStaffRisk = () => {},
  onLogin = null,
  user = null,
  userRole = ''
}) => {
  const [activeTab, setActiveTab] = useState('guided'); // 'guided' | 'quick-report' | 'history' | 'track'
  const [submittedReport, setSubmittedReport] = useState(null);
  const [copiedId, setCopiedId] = useState(false);
  const [copiedCardId, setCopiedCardId] = useState(null);
  const [showSeverityGuide, setShowSeverityGuide] = useState(false);

  // History State
  const [historyReports, setHistoryReports] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all' | 'pending' | 'investigating' | 'resolved'
  const [expandedCaseId, setExpandedCaseId] = useState(null);

  // Track State
  const [trackNumber, setTrackNumber] = useState('');
  const [trackResult, setTrackResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [trackError, setTrackError] = useState('');

  const isAdmin = userRole === 'admin' || (user?.email && ADMIN_EMAILS.some(e => e.toLowerCase() === user.email.toLowerCase()));
  const isHistoryView = activeTab === 'history' || activeTab === 'track';
  // Only show sticky header for admin users to maintain ultra-compact mobile layout for regular users
  const showStickyHeader = isAdmin && activeTab !== 'quick-report';

  // โหลดรายการประวัติความเสี่ยง
  const loadHistoryReports = useCallback(async () => {
    setIsLoadingHistory(true);
    let loaded = false;

    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('dental_risk_reports')
          .select('*')
          .order('created_at', { ascending: false });

        if (!error && Array.isArray(data)) {
          setHistoryReports(data);
          try {
            localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(data));
          } catch (e) {}
          loaded = true;
        }
      } catch (err) {
        console.warn('Failed to load history from Supabase:', err);
      }
    }

    if (!loaded) {
      try {
        const cached = localStorage.getItem(STORAGE_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setHistoryReports(parsed);
            loaded = true;
          }
        }
      } catch (e) {}
    }

    if (!loaded) {
      setHistoryReports(SAMPLE_INITIAL_REPORTS || []);
    }

    setIsLoadingHistory(false);
  }, []);

  useEffect(() => {
    if (isHistoryView && historyReports.length === 0) {
      loadHistoryReports();
    }
  }, [isHistoryView, historyReports.length, loadHistoryReports]);

  // คัดกรองตามสิทธิ์ Role:
  // - Admin: เห็นทั้งหมด ทั้งเปิดเผยตัวตนและนิรนาม
  // - User ทั่วไป: เห็นเฉพาะรายงานตนเองที่เปิดเผยตัวตน (is_anonymous !== true)
  // - Guest (ไม่ล็อกอิน): แสดงรายการว่าง พร้อมข้อความแนะนำให้ล็อกอินหรือค้นหาด้วยรหัสเคส
  const baseRoleFilteredReports = useMemo(() => {
    if (!historyReports || historyReports.length === 0) return [];

    if (isAdmin) {
      return historyReports;
    }

    if (!user) {
      return [];
    }

    const myUsername = (user.username || user.name || '').toLowerCase().trim();
    const myEmail = (user.email || '').toLowerCase().trim();
    const myId = user.id ? String(user.id).trim() : '';

    return historyReports.filter((item) => {
      if (item.is_anonymous === true) return false;
      const reporterName = (item.reporter_name || '').toLowerCase().trim();
      const contact = (item.contact_info || '').toLowerCase().trim();
      const reporterId = item.reporter_id || item.user_id ? String(item.reporter_id || item.user_id).trim() : '';

      return Boolean(
        (myUsername && (reporterName.includes(myUsername) || myUsername.includes(reporterName))) ||
        (myEmail && (reporterName.includes(myEmail) || contact.includes(myEmail))) ||
        (myId && reporterId === myId)
      );
    });
  }, [historyReports, isAdmin, user]);

  const displayedHistoryReports = useMemo(() => {
    let list = baseRoleFilteredReports;

    if (historyFilter !== 'all') {
      if (historyFilter === 'pending') {
        list = list.filter((r) => r.status === 'pending');
      } else if (historyFilter === 'investigating') {
        list = list.filter((r) => r.status === 'investigating' || r.status === 'in_progress');
      } else if (historyFilter === 'resolved') {
        list = list.filter((r) => r.status === 'action_taken' || r.status === 'closed' || r.status === 'resolved');
      }
    }

    return list;
  }, [baseRoleFilteredReports, historyFilter]);

  const counts = useMemo(() => {
    const all = baseRoleFilteredReports.length;
    const pending = baseRoleFilteredReports.filter((r) => r.status === 'pending').length;
    const investigating = baseRoleFilteredReports.filter((r) => r.status === 'investigating' || r.status === 'in_progress').length;
    const resolved = baseRoleFilteredReports.filter((r) => r.status === 'action_taken' || r.status === 'closed' || r.status === 'resolved').length;
    return { all, pending, investigating, resolved };
  }, [baseRoleFilteredReports]);

  // ค้นหาเคสเพื่อติดตามสถานะ (คัดกรองเฉพาะข้อมูลสาธารณะ ไม่เปิดเผยข้อมูล HN หรือผู้ป่วย)
  const handleSearchIncident = async (e) => {
    if (e) e.preventDefault();
    const query = trackNumber.trim();
    if (!query) return;

    setIsSearching(true);
    setTrackError('');
    setTrackResult(null);

    try {
      let found = null;

      // 0. หาจากประวัติที่โหลดมาแล้ว
      if (historyReports && historyReports.length > 0) {
        found = historyReports.find(
          (item) => item.incident_number?.toLowerCase() === query.toLowerCase()
        );
      }

      // 1. ค้นหาจาก Supabase
      if (!found && supabase) {
        const { data, error } = await supabase
          .from('dental_risk_reports')
          .select('incident_number, incident_date, risk_domain, title, status, severity_level, location, corrective_action, created_at, closed_at')
          .eq('incident_number', query)
          .maybeSingle();

        if (!error && data) {
          found = data;
        }
      }

      // 2. Fallback: ค้นหาจาก LocalStorage Cache
      if (!found) {
        try {
          const cached = localStorage.getItem(STORAGE_CACHE_KEY);
          if (cached) {
            const list = JSON.parse(cached);
            found = list.find((item) => item.incident_number?.toLowerCase() === query.toLowerCase());
          }
        } catch (err) {}
      }

      // 3. Fallback: ค้นหาจาก SAMPLE_INITIAL_REPORTS
      if (!found && Array.isArray(SAMPLE_INITIAL_REPORTS)) {
        found = SAMPLE_INITIAL_REPORTS.find(
          (item) => item.incident_number?.toLowerCase() === query.toLowerCase()
        );
      }

      if (found) {
        setTrackResult(found);
      } else {
        setTrackError(`ไม่พบข้อมูลอุบัติการณ์รหัส "${query}" กรุณาตรวจสอบรหัสอีกครั้ง`);
      }
    } catch (err) {
      console.warn('Track incident error:', err);
      setTrackError('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsSearching(false);
    }
  };

  // บันทึกรายงานจาก Guided Wizard ลงฐานข้อมูล Supabase และ Local Cache
  const handleWizardSubmit = async (formData) => {
    const now = new Date();
    const yy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const randomSuffix = Math.floor(100 + Math.random() * 900);
    const incidentNumber = `D-RSK-${yy}${mm}-${randomSuffix}`;
    const finalIncidentDate = formData.incident_date
      ? new Date(formData.incident_date).toISOString()
      : now.toISOString();

    const recordToInsert = {
      incident_number: incidentNumber,
      incident_date: finalIncidentDate,
      location: formData.location || 'คลินิกทันตกรรม',
      risk_domain: formData.risk_domain || 'clinical',
      category: formData.category || 'ทั่วไป',
      sub_category: formData.sub_category || '',
      severity_level: formData.severity_level || 'C',
      severity_score: ['A', 'B', '1'].includes(String(formData.severity_level).toUpperCase())
        ? 1
        : ['C', 'D', '2'].includes(String(formData.severity_level).toUpperCase())
        ? 2
        : ['E', 'F', '3'].includes(String(formData.severity_level).toUpperCase())
        ? 3
        : ['G', 'H', '4'].includes(String(formData.severity_level).toUpperCase())
        ? 4
        : 5,
      title: (formData.title || '').trim() || 'รายงานอุบัติการณ์',
      description: (formData.description || formData.title || '').trim(),
      immediate_action: (formData.immediate_action || '').trim(),
      contributing_factors: (formData.contributing_factors || '').trim(),
      is_patient_involved: !formData.no_patient_involved && Boolean(formData.patient_hn),
      patient_hn: formData.no_patient_involved ? '' : (formData.patient_hn || ''),
      procedure_name: formData.procedure_name || '',
      instrument_barcode: formData.no_instrument_data ? null : (formData.instrument_barcode || null),
      sterile_cycle_id: formData.sterile_cycle_id || null,
      machine_id: formData.machine_id || null,
      image_urls: Array.isArray(formData.images) ? formData.images : [],
      audio_note_url: formData.audio_note_url || null,
      is_anonymous: Boolean(formData.is_anonymous),
      reporter_name: formData.is_anonymous ? 'นิรนาม (Anonymous)' : (formData.reporter_name || user?.name || user?.email || 'บุคลากรทันตกรรม'),
      reporter_role: formData.is_anonymous ? '-' : (formData.reporter_role || userRole || 'เจ้าหน้าที่'),
      contact_info: formData.is_anonymous ? '-' : (formData.contact_info || user?.email || '-'),
      status: 'pending',
      investigator_name: '',
      root_cause_analysis: '',
      rca_fishbone_category: null,
      corrective_action: '',
      recall_batch_triggered: false,
      created_at: now.toISOString(),
      last_updated: now.toISOString()
    };

    let savedRecord = recordToInsert;

    // 1. Supabase insert
    if (supabase) {
      try {
        const { data, error } = await supabase
          .from('dental_risk_reports')
          .insert([recordToInsert])
          .select();

        if (!error && data && data.length > 0) {
          savedRecord = data[0];
        } else {
          console.warn('[RiskPortal] Supabase insert warning:', error?.message);
        }
      } catch (err) {
        console.warn('[RiskPortal] Supabase insert error, falling back to cache:', err);
      }
    }

    // 2. Sync LocalStorage Cache & State
    try {
      const cached = localStorage.getItem(STORAGE_CACHE_KEY);
      const parsed = cached ? JSON.parse(cached) : [];
      const updated = [savedRecord, ...parsed.filter((r) => r.incident_number !== savedRecord.incident_number)];
      localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(updated));
      setHistoryReports(updated);
    } catch (cErr) {
      console.warn('Failed to update local cache:', cErr);
    }

    setSubmittedReport(savedRecord);
    return savedRecord;
  };

  // คัดลอกรหัสอุบัติการณ์
  const handleCopyIncidentId = (idToCopy) => {
    if (!idToCopy) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(idToCopy);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2500);
    }
  };

  const handleCopyCardId = (idToCopy) => {
    if (!idToCopy) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(idToCopy);
      setCopiedCardId(idToCopy);
      setTimeout(() => setCopiedCardId(null), 2000);
    }
  };

  const renderStepper = (status) => {
    const currentStep = getStatusConfig(status)?.step || 1;
    return (
      <div style={{ margin: '12px 0 16px 0', padding: '12px 10px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
          {['รอตรวจสอบ', 'กำลังสอบสวน RCA', 'แก้ไขแล้ว', 'ปิดเคส'].map((stepLabel, idx) => {
            const isCompleted = currentStep >= idx + 1;
            const isCurrent = currentStep === idx + 1;
            return (
              <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <div
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    backgroundColor: isCompleted ? '#059669' : '#e2e8f0',
                    color: isCompleted ? '#ffffff' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.78em',
                    fontWeight: '700',
                    border: isCurrent ? '2.5px solid #6ee7b7' : 'none',
                    zIndex: 2
                  }}
                >
                  {isCompleted ? '✓' : idx + 1}
                </div>
                <span
                  style={{
                    fontSize: '0.68em',
                    color: isCompleted ? '#065f46' : '#94a3b8',
                    marginTop: '4px',
                    fontWeight: isCurrent ? '700' : '500',
                    textAlign: 'center'
                  }}
                >
                  {stepLabel}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        backgroundImage: 'radial-gradient(#e2e8f0 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        color: '#0f172a',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}
    >
      {/* 👑 Admin Mode Bar if logged in as Admin */}
      {isAdmin && (
        <div
          style={{
            backgroundColor: '#1e293b',
            color: '#f8fafc',
            padding: '8px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.86rem',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            flexWrap: 'wrap',
            gap: '10px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.2em' }}>👑</span>
            <span style={{ fontWeight: '600' }}>มุมมองพอร์ทัลด่วน (Admin Portal View)</span>
            <span
              style={{
                background: '#dc2626',
                color: '#ffffff',
                fontSize: '0.72rem',
                padding: '2px 8px',
                borderRadius: '4px',
                fontWeight: 'bold',
                letterSpacing: '0.5px'
              }}
            >
              ADMIN
            </span>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
              กำลังดูหน้าแบบฟอร์มรายงานของเจ้าหน้าที่หน้างาน
            </span>
          </div>

          <button
            type="button"
            onClick={onNavigateToStaffRisk}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 16px',
              borderRadius: '8px',
              backgroundColor: '#dc2626',
              color: '#ffffff',
              border: 'none',
              fontSize: '0.84rem',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 6px rgba(220, 38, 38, 0.3)'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#b91c1c'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; }}
          >
            <span>🛡️</span>
            <span>กลับไปหน้าจัดการความเสี่ยง (Admin Dashboard)</span>
          </button>
        </div>
      )}

      {/* Top Banner Navigation Bar (Visible for Admin, or in Track tab) */}
      {showStickyHeader && (
        <header
          style={{
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
          }}
        >
          <div
            style={{
              maxWidth: '960px',
              margin: '0 auto',
              padding: '12px 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap'
            }}
          >
            {/* Brand Logo & Name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #0d9488 0%, #0284c7 100%)',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px',
                  boxShadow: '0 3px 8px rgba(13, 148, 136, 0.3)'
                }}
              >
                🦷
              </div>
              <div>
                <div style={{ fontWeight: '800', fontSize: '1.02em', color: '#0f172a', lineHeight: '1.2' }}>
                  ระบบบริหารความเสี่ยงทันตกรรมและงานจ่ายกลาง
                </div>
                <div style={{ fontSize: '0.74em', color: '#64748b' }}>
                  Dental & CSSD Risk Portal • โรงพยาบาลทันตกรรม
                </div>
              </div>
            </div>

            {/* Staff Login / Full Dashboard Switch & Guide Button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setShowSeverityGuide(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  borderRadius: '10px',
                  backgroundColor: '#eff6ff',
                  color: '#1d4ed8',
                  border: '1.5px solid #bfdbfe',
                  fontSize: '0.84em',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dbeafe';
                  e.currentTarget.style.borderColor = '#93c5fd';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#eff6ff';
                  e.currentTarget.style.borderColor = '#bfdbfe';
                }}
              >
                <span>📖</span>
                <span>เกณฑ์ความรุนแรง & RCA</span>
              </button>

              {user ? (
                <button
                  type="button"
                  onClick={onNavigateToStaffRisk}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    borderRadius: '10px',
                    backgroundColor: isAdmin ? '#dc2626' : '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: '0.84em',
                    fontWeight: '700',
                    cursor: 'pointer',
                    boxShadow: isAdmin ? '0 2px 6px rgba(220, 38, 38, 0.3)' : '0 2px 6px rgba(2, 132, 199, 0.25)',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isAdmin ? '#b91c1c' : '#0369a1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isAdmin ? '#dc2626' : '#0284c7';
                  }}
                >
                  <span>{isAdmin ? '🛡️' : '📊'}</span>
                  <span>{isAdmin ? '🛡️ กลับไปหน้าจัดการความเสี่ยง (Admin Dashboard)' : 'แดชบอร์ดจัดการความเสี่ยง (Staff)'}</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (onLogin) onLogin();
                    else onNavigateToStaffRisk();
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 14px',
                    borderRadius: '10px',
                    backgroundColor: '#ffffff',
                    color: '#334155',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.84em',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <span>🔐</span>
                  <span>สำหรับเจ้าหน้าที่ / กรรมการความเสี่ยง</span>
                </button>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Main Content Area */}
      <main
        style={{
          maxWidth: '960px',
          margin: '0 auto',
          padding: activeTab === 'quick-report' ? '12px 10px 30px 10px' : showStickyHeader ? '20px 16px 50px 16px' : '16px 14px 40px 14px'
        }}
      >
        {/* Hero Section for Admins only */}
        {isAdmin && activeTab !== 'quick-report' && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                borderRadius: '20px',
                backgroundColor: '#ecfdf5',
                border: '1px solid #a7f3d0',
                color: '#047857',
                fontSize: '0.78em',
                fontWeight: '700',
                marginBottom: '8px'
              }}
            >
              <span>🛡️</span>
              <span>2P Safety & มาตรฐานห้องฆ่าเชื้อ CSSD Hospital Standard</span>
            </div>

            <h1 style={{ margin: '0 0 6px 0', fontSize: '1.6em', fontWeight: '900', color: '#0f172a', letterSpacing: '-0.02em' }}>
              Dental & CSSD Risk Reporting Portal
            </h1>
            <p style={{ margin: 0, fontSize: '0.88em', color: '#475569', maxWidth: '620px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.5' }}>
              ช่องทางรายงานอุบัติการณ์และความเสี่ยงทางคลินิกทันตกรรมและงานจ่ายกลางแบบรวดเร็ว
              ช่วยยกระดับความปลอดภัยของผู้ป่วยและประสิทธิภาพการฆ่าเชื้อเครื่องมือ
            </p>

            {/* Prominent Severity Guide & RCA Criteria Button */}
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setShowSeverityGuide(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 18px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                  border: '1.5px solid #93c5fd',
                  color: '#1e40af',
                  fontSize: '0.86em',
                  fontWeight: '800',
                  cursor: 'pointer',
                  boxShadow: '0 3px 10px rgba(37, 99, 235, 0.12)',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>📖</span>
                <span>คู่มือเกณฑ์ระดับความรุนแรง (Level A-I / 1-5) & เกณฑ์เวลาทำ RCA</span>
                <span
                  style={{
                    backgroundColor: '#2563eb',
                    color: '#ffffff',
                    fontSize: '0.72em',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontWeight: '700'
                  }}
                >
                  มาตรฐาน 2P Safety ↗
                </span>
              </button>
            </div>
          </div>
        )}

        {/* ==================== VIEW 1: GUIDED STEP-BY-STEP ==================== */}
        {activeTab === 'guided' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px', padding: '0 4px' }}>
              <button
                type="button"
                onClick={() => setActiveTab('quick-report')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 12px',
                  borderRadius: '20px',
                  backgroundColor: '#ffffff',
                  color: '#0284c7',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.78em',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>⚡</span>
                <span>สลับไปรายงานด่วนหน้าเดียว</span>
              </button>
            </div>

            <GuidedRiskReportWizard
              onSwitchToQuickReport={() => setActiveTab('quick-report')}
              onSubmit={handleWizardSubmit}
            />
          </div>
        )}

        {/* ==================== VIEW 2: QUICK REPORT ==================== */}
        {activeTab === 'quick-report' && (
          <RiskQuickReportForm
            user={user}
            onSwitchToGuided={() => setActiveTab('guided')}
            onSuccess={(newReport) => {
              setSubmittedReport(newReport);
            }}
          />
        )}

        {/* ==================== VIEW 3: RISK HISTORY & TRACKING ==================== */}
        {isHistoryView && (
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              border: '1.5px solid #e2e8f0',
              padding: '24px 18px',
              maxWidth: '820px',
              margin: '0 auto',
              boxShadow: '0 10px 25px -5px rgba(0,0,0,0.04)'
            }}
          >
            {/* Top Navigation Bar */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
                flexWrap: 'wrap',
                gap: '10px'
              }}
            >
              <button
                type="button"
                onClick={() => setActiveTab('guided')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 14px',
                  borderRadius: '8px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  color: '#334155',
                  fontSize: '0.84em',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>←</span>
                <span>กลับไปหน้ารายงาน</span>
              </button>

              <button
                type="button"
                onClick={loadHistoryReports}
                disabled={isLoadingHistory}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 12px',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  fontSize: '0.8em',
                  fontWeight: '600',
                  cursor: isLoadingHistory ? 'not-allowed' : 'pointer'
                }}
              >
                <span>🔄</span>
                <span>{isLoadingHistory ? 'กำลังโหลด...' : 'รีเฟรชข้อมูล'}</span>
              </button>
            </div>

            {/* Breadcrumb / Title */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '1.4em' }}>{isAdmin ? '👑' : '📋'}</span>
                <h2 style={{ margin: 0, fontSize: '1.25em', fontWeight: '800', color: '#0f172a' }}>
                  {isAdmin
                    ? '👑 ประวัติและรายการรายงานอุบัติการณ์ทั้งหมด (Admin Overview)'
                    : user
                    ? '📋 ประวัติการรายงานความเสี่ยงของฉัน'
                    : '📋 ประวัติการรายงานความเสี่ยง'}
                </h2>
              </div>
              <p style={{ margin: 0, fontSize: '0.84em', color: '#64748b', lineHeight: '1.4' }}>
                {isAdmin
                  ? 'แสดงรายงานทั้งหมด ทั้งแบบระบุตัวตนและแบบนิรนาม เพื่อการวิเคราะห์และติดตาม RCA'
                  : user
                  ? 'แสดงเฉพาะรายงานที่คุณได้ส่งแบบเปิดเผยตัวตน เพื่อติดตามความคืบหน้าและการแก้ไขปัญหา'
                  : 'เข้าสู่ระบบเพื่อดูประวัติส่วนตัว หรือตรวจสอบความคืบหน้ารายเคสด้วยรหัสอ้างอิง'}
              </p>
            </div>

            {/* Compact Case Code Lookup Accordion / Search Box */}
            <div
              style={{
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                border: '1.5px solid #e2e8f0',
                padding: '16px 18px',
                marginBottom: '20px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <span style={{ fontSize: '1.1em' }}>🔍</span>
                <span style={{ fontWeight: '700', fontSize: '0.88em', color: '#1e293b' }}>
                  ค้นหาความคืบหน้ารายเคสด้วยรหัสอ้างอิง (เช่น D-RSK-2026...)
                </span>
              </div>
              <form onSubmit={handleSearchIncident} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="ระบุรหัสอุบัติการณ์ เช่น D-RSK-202609-001"
                  value={trackNumber}
                  onChange={(e) => setTrackNumber(e.target.value)}
                  style={{
                    flex: 1,
                    minWidth: '220px',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.9em',
                    fontFamily: 'monospace'
                  }}
                />
                <button
                  type="submit"
                  disabled={isSearching}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    backgroundColor: '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    fontWeight: '700',
                    fontSize: '0.88em',
                    cursor: isSearching ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 6px rgba(2, 132, 199, 0.25)'
                  }}
                >
                  {isSearching ? 'กำลังค้นหา...' : 'ค้นหา'}
                </button>
              </form>

              {trackError && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '10px 14px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fca5a5',
                    borderRadius: '10px',
                    color: '#b91c1c',
                    fontSize: '0.84em',
                    fontWeight: '600'
                  }}
                >
                  ⚠️ {trackError}
                </div>
              )}

              {trackResult && (
                <div
                  style={{
                    marginTop: '14px',
                    border: '1.5px solid #bae6fd',
                    borderRadius: '12px',
                    backgroundColor: '#f0f9ff',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <span style={{ fontSize: '0.78em', color: '#64748b', fontWeight: '600' }}>ผลการค้นหาเคส:</span>
                      <div style={{ fontSize: '1.1em', fontWeight: '800', fontFamily: 'monospace', color: '#0f172a' }}>
                        {trackResult.incident_number}
                      </div>
                    </div>
                    {(() => {
                      const cfg = getStatusConfig(trackResult.status);
                      return (
                        <span
                          style={{
                            padding: '5px 12px',
                            borderRadius: '20px',
                            backgroundColor: cfg.bg,
                            color: cfg.color,
                            border: `1px solid ${cfg.color}30`,
                            fontWeight: '700',
                            fontSize: '0.82em',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                        >
                          <span>{cfg.icon}</span>
                          <span>{cfg.label}</span>
                        </span>
                      );
                    })()}
                  </div>

                  {/* Stepper Progress Bar */}
                  {renderStepper(trackResult.status)}

                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      border: '1px solid #e2e8f0',
                      fontSize: '0.86em',
                      lineHeight: '1.5'
                    }}
                  >
                    <div style={{ fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>
                      หัวข้อ: {trackResult.title}
                    </div>
                    <div style={{ color: '#475569', fontSize: '0.8em' }}>
                      📍 สถานที่: {trackResult.location} • วันที่รายงาน: {new Date(trackResult.created_at || trackResult.incident_date).toLocaleString('th-TH')}
                    </div>

                    {trackResult.corrective_action && (
                      <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                        <div style={{ fontWeight: '700', color: '#15803d', fontSize: '0.84em', marginBottom: '2px' }}>
                          🛡️ มาตรการแก้ไขและป้องกัน (CAPA):
                        </div>
                        <div style={{ color: '#166534', fontSize: '0.82em' }}>
                          {trackResult.corrective_action}
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ fontSize: '0.74em', color: '#94a3b8', textAlign: 'center' }}>
                    🔒 เพื่อรักษาความเป็นส่วนตัวตามมาตรฐาน PDPA ระบบจะไม่แสดงข้อมูลระบุตัวตนของผู้ป่วยหรือผู้รายงาน
                  </div>
                </div>
              )}
            </div>

            {/* Guest Banner if not logged in */}
            {!user && (
              <div
                style={{
                  backgroundColor: '#eff6ff',
                  border: '1.5px solid #bfdbfe',
                  borderRadius: '14px',
                  padding: '16px 18px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '1.6em' }}>ℹ️</span>
                  <div>
                    <div style={{ fontWeight: '700', color: '#1e40af', fontSize: '0.9em' }}>
                      ท่านกำลังเข้าถึงในโหมดผู้ใช้ทั่วไป (ยังไม่ได้เข้าสู่ระบบ)
                    </div>
                    <div style={{ color: '#3b82f6', fontSize: '0.82em', marginTop: '2px' }}>
                      กรุณาเข้าสู่ระบบเพื่อดูประวัติการรายงานความเสี่ยงที่ระบุตัวตนของคุณ หรือใช้ช่องค้นหาด้านบนเพื่อติดตามสถานะด้วยรหัสเคส
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (onLogin) onLogin();
                    else onNavigateToStaffRisk();
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    backgroundColor: '#2563eb',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '700',
                    fontSize: '0.84em',
                    cursor: 'pointer'
                  }}
                >
                  <span>🔐</span>
                  <span>เข้าสู่ระบบ</span>
                </button>
              </div>
            )}

            {/* Filter Tabs (Logged in user or Admin) */}
            {(user || isAdmin) && (
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '16px',
                  overflowX: 'auto',
                  paddingBottom: '4px'
                }}
              >
                {[
                  { key: 'all', label: `ทั้งหมด (${counts.all})` },
                  { key: 'pending', label: `⏳ รอตรวจสอบ (${counts.pending})` },
                  { key: 'investigating', label: `🔍 กำลังสอบสวน RCA (${counts.investigating})` },
                  { key: 'resolved', label: `✅ แก้ไข/ปิดเคสแล้ว (${counts.resolved})` }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setHistoryFilter(tab.key)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '0.82em',
                      fontWeight: '700',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      backgroundColor: historyFilter === tab.key ? '#0284c7' : '#ffffff',
                      color: historyFilter === tab.key ? '#ffffff' : '#475569',
                      border: historyFilter === tab.key ? '1px solid #0284c7' : '1px solid #cbd5e1',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Reports List */}
            {isLoadingHistory ? (
              <div style={{ textAlign: 'center', padding: '36px 0', color: '#64748b', fontSize: '0.9em' }}>
                ⏳ กำลังโหลดประวัติการรายงานความเสี่ยง...
              </div>
            ) : (user || isAdmin) && displayedHistoryReports.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '36px 20px',
                  backgroundColor: '#ffffff',
                  borderRadius: '16px',
                  border: '1.5px dashed #cbd5e1',
                  marginBottom: '20px'
                }}
              >
                <span style={{ fontSize: '2.4em' }}>📭</span>
                <h4 style={{ margin: '10px 0 6px 0', fontSize: '1.05em', color: '#334155', fontWeight: '800' }}>
                  {isAdmin
                    ? 'ไม่พบรายงานความเสี่ยงในตัวกรองนี้'
                    : 'ยังไม่มีประวัติการรายงานความเสี่ยง ที่เปิดเผยตัวตนของคุณ'}
                </h4>
                <p style={{ margin: '0 0 16px 0', fontSize: '0.84em', color: '#64748b' }}>
                  {isAdmin
                    ? 'ยังไม่มีข้อมูลรายงานที่ตรงกับสถานะที่เลือก'
                    : 'รายงานที่คุณส่งแบบเปิดเผยตัวตนจะถูกรวบรวมไว้ที่นี่ เพื่อให้คุณติดตามผลการแก้ไขและการตอบสนองได้ตลอดเวลา'}
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab('guided')}
                  style={{
                    padding: '9px 18px',
                    borderRadius: '10px',
                    backgroundColor: '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    fontWeight: '700',
                    fontSize: '0.86em',
                    cursor: 'pointer',
                    boxShadow: '0 2px 6px rgba(2, 132, 199, 0.25)'
                  }}
                >
                  ✍️ เริ่มเขียนรายงานใหม่
                </button>
              </div>
            ) : (
              (user || isAdmin) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {displayedHistoryReports.map((report) => {
                    const reportKey = report.id || report.incident_number;
                    const isExpanded = expandedCaseId === reportKey;
                    const st = getStatusConfig(report.status);
                    const domain = DOMAIN_LABEL_MAP[report.risk_domain] || DOMAIN_LABEL_MAP.clinical;
                    const sev = getSeverityBadge(report.severity_level);
                    const isOwner = user && (
                      (user.name && report.reporter_name?.toLowerCase().includes(user.name.toLowerCase())) ||
                      (user.email && (report.reporter_name?.toLowerCase().includes(user.email.toLowerCase()) || report.contact_info?.toLowerCase().includes(user.email.toLowerCase())))
                    );

                    return (
                      <div
                        key={reportKey}
                        style={{
                          backgroundColor: '#ffffff',
                          borderRadius: '16px',
                          border: '1.5px solid #e2e8f0',
                          padding: '16px 18px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {/* Card Top: Code + Status Badge */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '0.94em', fontWeight: '800', fontFamily: 'monospace', color: '#0f172a' }}>
                              {report.incident_number}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleCopyCardId(report.incident_number)}
                              style={{
                                background: copiedCardId === report.incident_number ? '#dcfce7' : '#f1f5f9',
                                border: '1px solid ' + (copiedCardId === report.incident_number ? '#86efac' : '#cbd5e1'),
                                color: copiedCardId === report.incident_number ? '#16a34a' : '#475569',
                                fontSize: '0.72em',
                                fontWeight: '700',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}
                              title="คัดลอกรหัสเคส"
                            >
                              {copiedCardId === report.incident_number ? '✓ คัดลอกแล้ว' : '📋 คัดลอก'}
                            </button>
                          </div>

                          <span
                            style={{
                              padding: '4px 10px',
                              borderRadius: '14px',
                              backgroundColor: st.bg,
                              color: st.color,
                              border: `1px solid ${st.color}30`,
                              fontSize: '0.78em',
                              fontWeight: '700',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <span>{st.icon}</span>
                            <span>{st.label}</span>
                          </span>
                        </div>

                        {/* Title */}
                        <h4 style={{ margin: '8px 0 6px 0', fontSize: '1.02em', fontWeight: '800', color: '#0f172a' }}>
                          {report.title}
                        </h4>

                        {/* Badges row */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '6px',
                              backgroundColor: domain.bg,
                              color: domain.color,
                              border: `1px solid ${domain.border}`,
                              fontSize: '0.74em',
                              fontWeight: '600'
                            }}
                          >
                            {domain.label}
                          </span>

                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '6px',
                              backgroundColor: sev.bg,
                              color: sev.color,
                              border: `1px solid ${sev.border}`,
                              fontSize: '0.74em',
                              fontWeight: '700'
                            }}
                          >
                            {sev.label}
                          </span>

                          {report.is_anonymous ? (
                            <span
                              style={{
                                padding: '2px 8px',
                                borderRadius: '6px',
                                backgroundColor: '#f1f5f9',
                                color: '#64748b',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.74em',
                                fontWeight: '600'
                              }}
                            >
                              🔒 รายงานแบบนิรนาม
                            </span>
                          ) : (
                            <span
                              style={{
                                padding: '2px 8px',
                                borderRadius: '6px',
                                backgroundColor: '#ecfeff',
                                color: '#0e7490',
                                border: '1px solid #a5f3fc',
                                fontSize: '0.74em',
                                fontWeight: '600'
                              }}
                            >
                              👤 โดย: {report.reporter_name || 'เจ้าหน้าที่'}
                            </span>
                          )}
                        </div>

                        {/* Meta row */}
                        <div style={{ fontSize: '0.78em', color: '#64748b', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <span>🕒 {new Date(report.incident_date || report.created_at).toLocaleString('th-TH')}</span>
                          <span>📍 {report.location || 'ไม่ระบุสถานที่'}</span>
                        </div>

                        {/* Accordion Toggle */}
                        <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={() => setExpandedCaseId(isExpanded ? null : reportKey)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#0284c7',
                              fontSize: '0.8em',
                              fontWeight: '700',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 6px'
                            }}
                          >
                            <span>{isExpanded ? 'ย่อรายละเอียด ▴' : 'รายละเอียดและความคืบหน้า ▾'}</span>
                          </button>
                        </div>

                        {/* Expanded details */}
                        {isExpanded && (
                          <div
                            style={{
                              marginTop: '12px',
                              paddingTop: '12px',
                              borderTop: '1px solid #f1f5f9'
                            }}
                          >
                            {/* Stepper */}
                            {renderStepper(report.status)}

                            <div style={{ fontSize: '0.84em', color: '#334155', lineHeight: '1.5' }}>
                              {report.description && (
                                <div style={{ marginBottom: '6px' }}>
                                  <span style={{ fontWeight: '700', color: '#1e293b' }}>รายละเอียด: </span>
                                  {report.description}
                                </div>
                              )}
                              {report.immediate_action && (
                                <div style={{ marginBottom: '6px' }}>
                                  <span style={{ fontWeight: '700', color: '#1e293b' }}>การจัดการเบื้องต้น: </span>
                                  {report.immediate_action}
                                </div>
                              )}
                              {report.contributing_factors && (
                                <div style={{ marginBottom: '6px', color: '#475569' }}>
                                  <span style={{ fontWeight: '700', color: '#1e293b' }}>ปัจจัยส่งเสริม: </span>
                                  {report.contributing_factors}
                                </div>
                              )}
                              {report.root_cause_analysis && (
                                <div style={{ marginBottom: '6px', color: '#475569' }}>
                                  <span style={{ fontWeight: '700', color: '#1e293b' }}>วิเคราะห์สาเหตุ (RCA): </span>
                                  {report.root_cause_analysis}
                                </div>
                              )}

                              {report.corrective_action && (
                                <div
                                  style={{
                                    marginTop: '8px',
                                    padding: '10px 12px',
                                    backgroundColor: '#f0fdf4',
                                    borderRadius: '8px',
                                    border: '1px solid #bbf7d0'
                                  }}
                                >
                                  <div style={{ fontWeight: '700', color: '#15803d', fontSize: '0.82em', marginBottom: '2px' }}>
                                    🛡️ มาตรการแก้ไขและป้องกัน (CAPA):
                                  </div>
                                  <div style={{ color: '#166534', fontSize: '0.82em' }}>
                                    {report.corrective_action}
                                  </div>
                                </div>
                              )}

                              {/* Patient HN (Admin or Owner only) */}
                              {report.patient_hn && (isAdmin || isOwner) && (
                                <div style={{ marginTop: '8px', fontSize: '0.8em', color: '#64748b' }}>
                                  <span style={{ fontWeight: '700' }}>HN ผู้ป่วย: </span>
                                  {report.patient_hn}
                                </div>
                              )}

                              {/* CSSD Barcode */}
                              {report.instrument_barcode && (
                                <div style={{ marginTop: '4px', fontSize: '0.8em', color: '#64748b' }}>
                                  <span style={{ fontWeight: '700' }}>🏷️ Barcode เครื่องมือ: </span>
                                  {report.instrument_barcode}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        )}

        {/* Subtle Bottom Footer Tracking Link (when not on history view) */}
        {!isHistoryView && (
          <div style={{ textAlign: 'center', marginTop: '20px', paddingBottom: '24px' }}>
            <button
              type="button"
              onClick={() => setActiveTab('history')}
              style={{
                background: 'none',
                border: 'none',
                color: '#64748b',
                fontSize: '0.84em',
                fontWeight: '600',
                cursor: 'pointer',
                textDecoration: 'underline',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '8px',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#0284c7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#64748b'; }}
            >
              <span>📋</span>
              <span>คลิกเพื่อดูประวัติการรายงานความเสี่ยง</span>
            </button>
          </div>
        )}
      </main>

      {/* Success Modal After Reporting */}
      {submittedReport && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px'
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              padding: '28px 24px',
              maxWidth: '440px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1.5px solid #e2e8f0'
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#dcfce7',
                color: '#16a34a',
                fontSize: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px auto',
                boxShadow: '0 4px 10px rgba(22, 163, 74, 0.2)'
              }}
            >
              ✓
            </div>

            <h3 style={{ margin: '0 0 6px 0', fontSize: '1.3em', fontWeight: '800', color: '#0f172a' }}>
              ส่งรายงานอุบัติการณ์เรียบร้อยแล้ว
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '0.84em', color: '#64748b' }}>
              ข้อมูลถูกส่งไปยังกรรมการความเสี่ยงและทีมงานที่เกี่ยวข้องเพื่อตรวจสอบทันที
            </p>

            {/* Incident Code Badge */}
            <div
              style={{
                backgroundColor: '#f8fafc',
                border: '1.5px dashed #cbd5e1',
                borderRadius: '12px',
                padding: '12px',
                marginBottom: '16px'
              }}
            >
              <div style={{ fontSize: '0.74em', color: '#64748b', fontWeight: '600', marginBottom: '2px' }}>
                รหัสอุบัติการณ์ของท่าน (Incident Tracking ID):
              </div>
              <div style={{ fontSize: '1.25em', fontWeight: '900', fontFamily: 'monospace', color: '#0284c7' }}>
                {submittedReport.incident_number}
              </div>

              <button
                type="button"
                onClick={() => handleCopyIncidentId(submittedReport.incident_number)}
                style={{
                  marginTop: '8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '5px 12px',
                  borderRadius: '6px',
                  backgroundColor: copiedId ? '#dcfce7' : '#ffffff',
                  color: copiedId ? '#16a34a' : '#334155',
                  border: copiedId ? '1px solid #86efac' : '1px solid #cbd5e1',
                  fontSize: '0.78em',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                <span>{copiedId ? '✓ คัดลอกสำเร็จ' : '📋 คัดลอกรหัส'}</span>
              </button>
            </div>

            <p style={{ fontSize: '0.76em', color: '#64748b', marginBottom: '20px', lineHeight: '1.4' }}>
              ท่านสามารถใช้รหัสนี้ตรวจสอบสถานะการสอบสวนและการแก้ไขได้ในหน้า "ประวัติการรายงานความเสี่ยง"
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                type="button"
                onClick={() => {
                  setTrackNumber(submittedReport.incident_number);
                  setTrackResult(submittedReport);
                  setSubmittedReport(null);
                  setActiveTab('history');
                }}
                style={{
                  padding: '12px',
                  borderRadius: '10px',
                  backgroundColor: '#0284c7',
                  color: '#ffffff',
                  border: 'none',
                  fontWeight: '700',
                  fontSize: '0.9em',
                  cursor: 'pointer'
                }}
              >
                🔍 ดูสถานะเคสนี้ทันที
              </button>

              <button
                type="button"
                onClick={() => setSubmittedReport(null)}
                style={{
                  padding: '10px',
                  borderRadius: '10px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  fontWeight: '600',
                  fontSize: '0.86em',
                  cursor: 'pointer'
                }}
              >
                ✍️ รายงานเคสใหม่เพิ่มเติม
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Severity Guide Modal */}
      <SeverityGuideModal
        isOpen={showSeverityGuide}
        onClose={() => setShowSeverityGuide(false)}
        initialTab="clinical"
      />
    </div>
  );
};

export default RiskPortalStandalonePage;
